import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authAPI, adminAPI, pendingRegistrationStore, impersonationStore, tokenStore } from '../lib/api';
import SessionTimeoutManager from '../components/SessionTimeoutManager';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [player, setPlayer] = useState(null);
  const [hasLadderAccess, setHasLadderAccess] = useState(false);
  const [profileCompleted, setProfileCompleted] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  // EM-3: session IDs this user is an Event Manager for. Empty by default.
  // Drives the "Manage Event" pill on My Events rows + the page-level
  // guard on AdminEventEditorPage. Admins ignore this list because their
  // role check is independent (admin sees every session).
  const [managedSessions, setManagedSessions] = useState([]);
  // Universal Event Manager (Feb 2026) — array of {event_kind, event_id}
  // this profile currently manages across ALL event types. Drives the
  // Manage Event pill on discovery / My Events / Home / detail pages.
  const [managedEvents, setManagedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Impersonation banner state — mirrors impersonationStore (localStorage).
  const [impersonation, setImpersonation] = useState(() => impersonationStore.get());

  const applyAuth = useCallback((data) => {
    setUser(data?.user ?? null);
    setPlayer(data?.player ?? null);
    setHasLadderAccess(!!data?.has_ladder_access);
    setProfileCompleted(!!data?.profile_completed);
    setManagedSessions(Array.isArray(data?.managed_session_ids) ? data.managed_session_ids : []);
    setManagedEvents(Array.isArray(data?.managed_events) ? data.managed_events : []);
    const status = data?.account_status ?? data?.user?.account_status ?? null;
    setAccountStatus(status);
    // Drop the cached pending-registration name once the user has moved
    // past the pending state (approved or denied path resolved).
    if (status && status !== 'pending_approval') pendingRegistrationStore.clear();
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await authAPI.getMe();
      applyAuth(res.data);
    } catch {
      applyAuth(null);
    } finally {
      setLoading(false);
    }
  }, [applyAuth]);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (userData, playerData, ladderAccess, profCompleted, managedSessionIds, managedEventsFromLogin) => {
    setUser(userData);
    setPlayer(playerData);
    setHasLadderAccess(ladderAccess ?? (userData?.role === 'admin' || playerData != null));
    setProfileCompleted(profCompleted ?? (userData?.role === 'admin' || (playerData?.profile_completed ?? false)));
    setAccountStatus(userData?.account_status ?? null);
    // EM-3 — sync managed sessions from the login response so the user's
    // Home / My Events shows their managed session immediately, without
    // waiting for a follow-up /auth/me. Callers that don't pass it (legacy
    // signup/join flows) leave the value alone; brand-new accounts can't
    // be EMs anyway.
    if (Array.isArray(managedSessionIds)) setManagedSessions(managedSessionIds);
    // Universal Event Manager (Feb 2026) — same principle for the
    // canonical managedEvents list. Without this line a freshly-logged-in
    // Event Manager sees NO Manage pill and gets bounced from the manage
    // route until the next hard page load re-hydrates /auth/me. This is
    // the "Aaron cannot manage his Tournament after login" regression.
    if (Array.isArray(managedEventsFromLogin)) setManagedEvents(managedEventsFromLogin);
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch { /* ignore */ }
    impersonationStore.clear();
    setImpersonation(null);
    applyAuth(null);
  };

  // Impersonation — start. Called by MembersPage after a successful
  // /admin/impersonate/{id}. Persists the impersonated name + return ticket so
  // the global banner + exit control work across reloads.
  const enterImpersonation = useCallback((data) => {
    const meta = {
      name: data?.name || 'member',
      returnTicket: data?.returnTicket || null,
    };
    impersonationStore.set(meta);
    setImpersonation(meta);
  }, []);

  // Impersonation — exit. Backend re-verifies the admin role from the signed
  // return ticket and mints a fresh admin session. Restores admin auth state.
  const exitImpersonation = useCallback(async () => {
    const meta = impersonationStore.get();
    if (!meta?.returnTicket) {
      impersonationStore.clear();
      setImpersonation(null);
      return false;
    }
    try {
      const res = await adminAPI.stopImpersonation(meta.returnTicket);
      impersonationStore.clear();
      setImpersonation(null);
      applyAuth(res.data); // session_token auto-persisted by the axios interceptor
      return true;
    } catch (e) {
      // Ticket expired / admin role lost — clear state; caller sends to /login.
      impersonationStore.clear();
      setImpersonation(null);
      throw e;
    }
  }, [applyAuth]);

  const refreshUser = async () => {
    try {
      const res = await authAPI.getMe();
      applyAuth(res.data);
    } catch { /* ignore */ }
  };

  // Listen for the global `tl:session-expired` event fired by the axios
  // 401 interceptor (lib/api.js). The interceptor already cleared the dead
  // token; here we clear the in-memory user and bounce to login with a
  // ?reason=timeout marker so LoginPage can show a friendly banner.
  useEffect(() => {
    const onExpired = () => {
      const onLoginPage = window.location.pathname === '/login';
      if (!onLoginPage) {
        // Hard-redirect FIRST so any in-flight React work is aborted
        // before ProtectedRoute can render a plain <Navigate to="/login"/>
        // (which would lose the reason=timeout banner).
        const next = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.replace(`/login?reason=timeout&next=${next}`);
        return;
      }
      // Already on /login — just drop the stale user from context.
      applyAuth(null);
    };
    window.addEventListener('tl:session-expired', onExpired);
    return () => window.removeEventListener('tl:session-expired', onExpired);
  }, [applyAuth]);

  // Local helper invoked by the timeout manager — wraps the regular logout
  // and, when the timeout fires naturally (`reason === 'expired'`), also
  // routes the user to /login?reason=timeout so the LoginPage banner shows.
  const handleTimeoutLogout = useCallback(async (reason) => {
    tokenStore.clear();
    try { await authAPI.logout(); } catch { /* ignore */ }
    applyAuth(null);
    if (reason === 'expired') {
      window.location.replace('/login?reason=timeout');
    } else {
      window.location.replace('/login');
    }
  }, [applyAuth]);

  return (
    <AuthContext.Provider value={{
      user, player, hasLadderAccess, profileCompleted, accountStatus,
      managedSessions, managedEvents,
      loading, login, logout, refreshUser, checkAuth,
      impersonation, enterImpersonation, exitImpersonation,
    }}>
      {children}
      <SessionTimeoutManager userId={user?.id || null} onLogout={handleTimeoutLogout} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
